import { useState, useEffect } from 'react';

import { TOUR_PAST_SEARCH } from '@constants/constants';
import { callService } from '@api/remote-call';
import { SearchViewModel, WebUrlType } from '@api/v3';
import { YearTabProps } from '@components/ui/search/SearchMonthDate/interface';
import { tour_ga4Event } from '@utils/ga4Event/tour_ga4Event';
import { handleFilterAndUpdateUrl } from '@utils/utils';
import clean from '@utils/cleanObject';
import SearchMonthDate from '@components/ui/search/SearchBarInputNext/SearchMonthDate';
import {
  getSafeLocalStorageItem,
  removeSafeLocalStorageItem,
  setSafeLocalStorageItem,
} from '@utils/localStorage/getSafeLocalStorage';
import { useAppRouter } from '@contexts/router';

import { BookerSearchDrawer } from './BookerSearchDrawer';
import { BookerSearchViewModel } from './BookerSearchViewModel';
import { useGetPopularSearches } from '../hooks';
import { MonthDateWrapper, WhereToGoSearchContainer } from './styled';

export interface TourBookerError {
  location?: boolean;
}
interface BookerWheretoGoProps {
  subtitle: string;
  onHrefUpdate?: (href: string) => void;
  onSearchCalled: () => void;
  onInputLocationChange?: (item: BookerSearchViewModel) => void;
  href: string;
  type: string;
  error?: TourBookerError;
  setError: React.Dispatch<
    React.SetStateAction<Partial<TourBookerError | undefined>>
  >;
  initialError?: TourBookerError;
  setInitialError: React.Dispatch<
    React.SetStateAction<Partial<TourBookerError | undefined>>
  >;
}
export const BookerWheretoGo = ({
  subtitle,
  onHrefUpdate,
  href,
  onSearchCalled,
  type,
  initialError,
  error,
  setError,
  setInitialError,
}: BookerWheretoGoProps) => {
  const [hrefProps, setHrefProps] = useState<{
    [key: string]: string | number | string[] | undefined;
  }>();
  const { items: popularSearchItems } = useGetPopularSearches({
    type: 'tour',
  });
  const [locations, setLocation] = useState<BookerSearchViewModel | null>(null);
  const [pastTourItems, setPastTourItems] = useState(
    getSafeLocalStorageItem(TOUR_PAST_SEARCH)
      ? JSON.parse(getSafeLocalStorageItem(TOUR_PAST_SEARCH) || '[]')
      : []
  );
  const [searchedItemText, setSearchedItemText] = useState('');

  const router = useAppRouter();

  const clearPastTourSearch = () => {
    removeSafeLocalStorageItem(TOUR_PAST_SEARCH);
    setPastTourItems([]);
  };
  const onInputChange = (item: BookerSearchViewModel) => {
    const props = {
      ...hrefProps,
      slug: item?.url,
      isBooker: 'true',
    };

    setHrefProps(props);
    setLocation(item);
  };
  const removeItemFromLocalStorage = (item: BookerSearchViewModel) => {
    const newItems = pastTourItems.filter(
      (i: BookerSearchViewModel) => i.url !== item.url
    );

    setPastTourItems(newItems);
    setSafeLocalStorageItem(TOUR_PAST_SEARCH, JSON.stringify(newItems));
  };

  // ===========================================
  // Update the next search base on the popular search item
  // ===========================================
  const handlePopularItemSelected = (item: SearchViewModel) => {
    onHrefUpdate?.(
      handleFilterAndUpdateUrl(
        clean({
          slug: item?.url,
        })
      )
    );
  };

  // ===========================================
  // Handel save items to local storage
  // ===========================================

  const saveItems = async () => {
    if (!href || !type) return;

    let pastTourItems = [];
    let tourItems: any[] = [];
    const updatedHref = href?.split?.('?')?.[0];

    switch (type) {
      case WebUrlType.TOUR_CITY:
      case WebUrlType.TOUR_TYPE:
      case WebUrlType.TOUR_THEME:
      case WebUrlType.TOUR_REGION:
      case WebUrlType.TOUR_COUNTRY:
      case WebUrlType.TOUR_CATEGORY:
      case WebUrlType.TOUR:
        if (getSafeLocalStorageItem(TOUR_PAST_SEARCH)) {
          pastTourItems = JSON.parse(
            getSafeLocalStorageItem(TOUR_PAST_SEARCH) || '[]'
          );
        }

        tourItems = pastTourItems ? [...pastTourItems] : [];

        if (!tourItems.find((i) => i.url === updatedHref)) {
          tourItems.unshift({
            id: Math.random(),
            text: searchedItemText,
            url: updatedHref,
            type: type,
          });
        } else {
          const target = tourItems.find((i) => i.url === locations?.url);

          const targetIndex = tourItems.indexOf(target);

          tourItems.splice(targetIndex, 1);
          tourItems.unshift({
            id: Math.random(),
            text: searchedItemText,
            url: updatedHref,
            type: type,
          });
        }

        setSafeLocalStorageItem(TOUR_PAST_SEARCH, JSON.stringify(tourItems));
        router.push(href);
        break;

      default:
        router.push(href);
    }
  };
  useEffect(() => {
    if (!href || !type) return;
    saveItems();
    const TourEventData = {
      destination_point: searchedItemText,
      date_range: selectedItems.join(','),
    };
    tour_ga4Event(TourEventData);
  }, [onSearchCalled]);

  useEffect(() => {
    if (!href.split('?')[0]) {
      setInitialError((val) => ({
        ...val,
        location: true,
      }));
    } else {
      setInitialError((val) => ({
        ...val,
        location: false,
      }));

      setError({
        ...initialError,
        location: false,
      });
    }
  }, [href, location]);

  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const onDateMonthsUpdated = (info: YearTabProps[]) => {
    const [currentYear, nextYear] = info;

    const selectedMonths: string[] = [];
    currentYear.months?.forEach((item) => {
      if (item.checked) {
        selectedMonths.push(`${currentYear.year}${item.number}`);
      }
    });

    nextYear.months?.forEach((item) => {
      if (item.checked) {
        selectedMonths.push(`${nextYear.year}${item.number}`);
      }
    });
    setSelectedItems(selectedMonths);
  };
  const currentHref = href?.split('?')?.[0];
  useEffect(() => {
    if (selectedItems.length === 0) return;

    onHrefUpdate?.(`${currentHref}?date=${selectedItems.join(',')}`);
  }, [currentHref, onHrefUpdate, selectedItems]);

  return (
    <div style={{ gap: '12px', display: 'flex', width: '100%' }}>
      <WhereToGoSearchContainer>
        <BookerSearchDrawer
          type='Tour'
          onSearchItemTextUpdate={setSearchedItemText}
          onHrefUpdate={onHrefUpdate}
          href={href}
          placeholder={subtitle}
          responsiveTitle={subtitle}
          fetchItems={(param: string) =>
            callService(
              {
                service: 'SearchesService',
                method: 'searchesTour',
                version: 'v3',
              },
              {
                s: param || '',
              }
            )
          }
          onInputLocationChange={onInputChange}
          history={pastTourItems?.slice(0, 3)}
          onClearClick={clearPastTourSearch}
          onRemoveItem={removeItemFromLocalStorage}
          error={error?.location}
          popularSearchItems={popularSearchItems}
          onPopularSearchItemSelected={handlePopularItemSelected}
        />
      </WhereToGoSearchContainer>
      <MonthDateWrapper>
        <SearchMonthDate onMonthsUpdated={onDateMonthsUpdated} />
      </MonthDateWrapper>
    </div>
  );
};
export default BookerWheretoGo;
